import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import {
    AbstractControl,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { RouterLink } from '@angular/router';
import { RegionApiFrom } from '@api-gen/models/region-api-from';
import { CountryCodeApiRoaming } from '@api-roaming/models/country-code-api-roaming';
import { GetConfcodeInfoResponseApiRoaming } from '@api-roaming/models/get-confcode-info-response-api-roaming';
import { ListCommunicChannelResponseApiRoaming } from '@api-roaming/models/list-communic-channel-response-api-roaming';
import { RegEmailRequestApiRoaming } from '@api-roaming/models/reg-email-request-api-roaming';
import { RegistrationRequestApiRoaming } from '@api-roaming/models/registration-request-api-roaming';
import { ErrorResponse } from '@api/models';
import { SysLimiters } from '@core/limiters/limiters.model';
import { NavItemTranslateCodes } from '@core/models/nav-item.model';
import { SettingsAsObject } from '@core/models/settings.model';
import { RouteEntity } from '@core/router/router.entity';
import { AppSettingsService } from '@core/services/app-settings.service';
import { TranslateModule } from '@ngx-translate/core';
import { CountrySelectorComponent } from '@shared/components/country-selector/country-selector.component';
import { FormErrorComponent } from '@shared/components/form/form-error/form-error.component';
import { PATTERN_EMAIL, PATTERN_NAME, SYS_OPT_DEFAULT_CODE } from '@shared/constants';
import { IMaskDirective } from 'angular-imask';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';


/**
 * Registration form
 */
@Component({
    selector: 'app-register-form',
    templateUrl: './register-form.component.html',
    styleUrls: ['./register-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatCardModule,
        FormErrorComponent,
        CountrySelectorComponent,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        TranslateModule,
        IMaskDirective,
        MatIconModule,
        MatSelectModule,
        MatCheckboxModule,
        FormsModule,
        RouterLink,
        MatButtonModule,
    ],
})
export class RegisterFormComponent implements OnInit, OnDestroy {
    /**
     * Default number code
     */
    @Input()
    public set defaultCode(code: CountryCodeApiRoaming) {
        if (code) {
            this.codeControl.setValue(code.code);
        }
    }

    /**
     * Instance settings object from DB
     */
    @Input()
    public set settings(settings: SettingsAsObject) {
        if (settings) {
            this.fields.email = settings.CLIENT_ATTR_EMAIL;
            this.fields.firstName = settings.CLIENT_ATTR_FIRST;
            this.fields.lastName = settings.CLIENT_ATTR_LAST;

            if (this.fields.email !== 'U') {
                this.emailControl.enable();

                if (this.fields.email === 'M') {
                    this.emailControl.setValidators([Validators.required, Validators.pattern(PATTERN_EMAIL)]);
                } else {
                    this.emailControl.clearValidators();
                }
            } else {
                this.emailControl.disable();
                this.emailControl.clearValidators();
            }

            if (this.fields.firstName !== 'U') {
                this.nameControl.enable();
                const nameValidators: ValidatorFn[] = [
                    ...(this.fields.firstName === 'M' ? [Validators.required] : []),
                    Validators.maxLength(150), Validators.pattern(PATTERN_NAME),
                ];

                this.nameControl.setValidators(nameValidators);
            } else {
                this.nameControl.disable();
                this.nameControl.clearValidators();
            }

            if (this.fields.lastName !== 'U') {
                this.surnameControl.enable();
                const nameValidators: ValidatorFn[] = [
                    ...(this.fields.lastName === 'M' ? [Validators.required] : []),
                    Validators.maxLength(150), Validators.pattern(PATTERN_NAME),
                ];

                this.surnameControl.setValidators(nameValidators);
            } else {
                this.surnameControl.disable();
                this.surnameControl.clearValidators();
            }
        }
    }

    public get codeControl(): AbstractControl {
        return this.form.get('code');
    }

    public get phoneControl(): AbstractControl {
        return this.form.get('phone');
    }

    public get nameControl(): AbstractControl {
        return this.form.get('name');
    }

    public get surnameControl(): AbstractControl {
        return this.form.get('surname');
    }

    public get emailControl(): AbstractControl {
        return this.form.get('email');
    }

    public get passwordControl(): AbstractControl {
        return this.form.get('password');
    }

    public get confirmControl(): AbstractControl {
        return this.form.get('confirmPassword');
    }

    public get regionControl(): AbstractControl {
        return this.form.get('region');
    }

    constructor(
        private appSettingsService: AppSettingsService,
        private recaptchaV3Service: ReCaptchaV3Service,
    ) {
    }

    /**
     * Minimum password length
     */
    public readonly passwordMinLength: number = SysLimiters.PasswordMinLength;
    /**
     * List of regions
     */
    @Input()
    public regions: RegionApiFrom[] = [];

    /**
     * List of country phone codes
     */
    @Input()
    public countryCodes: CountryCodeApiRoaming[] = [];

    @ViewChild('phoneInput', { static: true })
    public phoneInput: ElementRef<HTMLInputElement>;

    /**
     * Request process indicator
     */
    @Input()
    public pending: boolean;

    /**
     * Server Error Object
     */
    @Input()
    public error: ErrorResponse;

    /**
     * List of communication channels
     */
    @Input()
    public channels: ListCommunicChannelResponseApiRoaming[];

    /**
     * Label for select communication channels
     */
    @Input()
    public channelsText: GetConfcodeInfoResponseApiRoaming;

    /**
     * Form submission
     */
    @Output() public submitted: EventEmitter<RegistrationRequestApiRoaming & RegEmailRequestApiRoaming> =
        new EventEmitter<RegistrationRequestApiRoaming & RegEmailRequestApiRoaming>();

    /**
     * Error generating recaptcha token
     */
    @Output()
    public errorRecaptcha: EventEmitter<void> = new EventEmitter<void>();

    /**
     * Flag hide/show password
     */
    public hidePassword: boolean = true;
    public termsAccepted: boolean = false;

    public readonly routeEntity: typeof RouteEntity = RouteEntity;
    public readonly navItemTranslateCodes: typeof NavItemTranslateCodes = NavItemTranslateCodes;

    public fields: {
        [key: string]: string;
    } = {};

    readonly #codeActionRecaptcha: string = 'submitRegisterForm';

    private readonly destroy$: Subject<void> = new Subject<void>();

    public readonly minLength: number = SysLimiters.PasswordMinLength;
    public readonly maxLength: number = SysLimiters.PasswordMaxLength;

    public readonly passwordValidators: ValidatorFn[] =
        [Validators.required, Validators.maxLength(this.maxLength), Validators.minLength(this.minLength)];

    public readonly form: UntypedFormGroup = new UntypedFormGroup({
        code: new UntypedFormControl('', [Validators.required]),
        phone: new UntypedFormControl('', [Validators.required]),
        email: new UntypedFormControl('', [Validators.required, Validators.pattern(PATTERN_EMAIL)]),
        name: new UntypedFormControl('', { validators: [], updateOn: 'blur' }),
        surname: new UntypedFormControl('', { validators: [], updateOn: 'blur' }),
        registrToken: new UntypedFormControl('', []),
        password: new UntypedFormControl('', this.passwordValidators),
        confirmPassword: new UntypedFormControl('', [this.validatePasswordMatch.bind(this)]),
        region: new UntypedFormControl('', [Validators.required]),
        sysCode: new UntypedFormControl(SYS_OPT_DEFAULT_CODE, [Validators.required]),
    });

    private generateSendData(token: string): void {
        const prefix: string = ('00' + this.codeControl.value).slice(-3);
        this.submitted.emit({

            sysCode: this.form.get('sysCode').value,
            phone: prefix + this.phoneControl.value,
            email: this.emailControl.value || null,
            password: this.passwordControl.value,
            regId: this.regionControl.value,
            name: this.nameControl.value || null,
            surname: this.surnameControl.value || null
            ,
            ...(token ? { registrToken: token } : null),
        });
    }

    public ngOnInit(): void {
        this.error = null;
        this.passwordControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.confirmControl.updateValueAndValidity());
    }

    public onCodeSelect(): void {
        this.phoneInput.nativeElement.focus();
    }

    public onSubmit(): void {
        if (this.appSettingsService.getProp('APP_SETTINGS_RECAPTCHA_KEY')) {
            this.recaptchaV3Service.execute(this.#codeActionRecaptcha).subscribe(
                (token: string) => {
                    this.generateSendData(token);
                },
                () => {
                    this.errorRecaptcha.emit();
                },
            );
        } else {
            this.generateSendData(null);
        }
    }

    public validatePasswordMatch(control: AbstractControl): ValidationErrors | null {
        return this.form && control.value !== this.passwordControl.value ? { mismatch: true } : null;
    }

    public ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
